import React from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/detailsStyle';
import SelectAccess from 'views/Users/Details/UserDetails/dialogs/subcomponents/SelectAccess';
import DepartmentBadges from 'views/Users/Details/UserDetails/dialogs/subcomponents/DepartmentBadges';
import { Caption } from 'components/Typography/Typography';
import Business from '@material-ui/icons/Business';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import { AccountCell } from 'components/Cells/Cells';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IFn {
	handleIsUnlimited: (deptId?: string) => boolean;
	handleGetDate: (deptId?: string) => string;
	handleSetDate: (deptId: string, value?: any) => void;
}

interface IDepartmentsFn extends IFn {
	handleIsChecked: (deptId: string) => boolean;
	handleBadgeClick: (deptId: string, name: string, uiColor: string) => void;
}

interface IEditPermissionProps {
	hideDialog: () => void;
	account: any;
	data: types.users.IEditPermissionAccount | null;
	isEnterprise: boolean;
	accountFn: IFn;
	departmentsFn: IDepartmentsFn;
	handleSave: () => void;
}

const EditPermission = ({
	hideDialog,
	account,
	isEnterprise,
	handleSave,
	departmentsFn,
	data,
	accountFn,
}: IEditPermissionProps) => {
	const isAccountPermission = !!data?.account;
	const classes = useStyles();
	const [showIsValid, setShowIsValid] = React.useState(false);

	const isValid = (fn: IFn, ...props: any) =>
		(fn.handleIsUnlimited && fn.handleIsUnlimited(...props)) ||
		(!!fn.handleGetDate && fn.handleGetDate(...props) !== '');

	const isDepartmentValid = (deptId: string): boolean =>
		departmentsFn.handleIsChecked(deptId) && isValid(departmentsFn, deptId);

	const isAccountValid = () => isValid(accountFn);

	return (
		<CustomDialog
			isOpen
			hideDialog={hideDialog}
			header={translate('users.details.editPermissions')}
			dialogBody={
				<div className={classes.dialogBody}>
					<ConditionalRender
						show={isEnterprise}
						onTrue={() => (
							<>
								<Caption color="secondary-light">{translate('organization.name')}</Caption>
								<AccountCell
									text={account.accountName}
									icon={
										<CustomAvatar
											accountId={account.accountId}
											placeholderIcon={<Business />}
											styles={{ width: 16, marginRight: 5 }}
										/>
									}
								/>
							</>
						)}
					/>
					<ConditionalRender
						show={isAccountPermission}
						onTrue={() => (
							<SelectAccess
								isUnlimited={accountFn.handleIsUnlimited()}
								date={accountFn.handleGetDate()}
								setDate={accountFn.handleSetDate}
								error={showIsValid && !isAccountValid()}
							/>
						)}
						onFalse={() => (
							<DepartmentBadges
								{...departmentsFn}
								showIsValid={showIsValid}
								isDepartmentValid={isDepartmentValid}
								departments={account.departments}
							/>
						)}
					/>
				</div>
			}
			rightSideButtons={[
				{
					text: translate('cancel'),
					color: 'tertiary-light',
					onClick: hideDialog,
					id: 'cancel-button',
				},
				{
					text: translate('save'),
					color: 'primary-light',
					onClick: () => {
						if (data) {
							setShowIsValid(true);
							const departmentsValid = Object.keys(data.departments).every((deptId) =>
								isDepartmentValid(deptId)
							);
							if (isAccountPermission) {
								isAccountValid() && handleSave();
							} else if (departmentsValid) {
								handleSave();
							}
						}
					},
				},
			]}
		/>
	);
};

export default EditPermission;
